import { Loader2 } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Field, SelectField } from "../components/site/Field";
import { FormSuccess } from "../components/site/FormSuccess";
import { Eyebrow, Reveal } from "../components/site/motion";
import { useSubmitForm } from "../hooks/useForm";

const INITIAL = { business_name: "", business_type: "", contact_name: "", email: "", phone: "", location: "", estimated_volume: "", message: "" };
const TYPES = ["Retailer", "Gym", "Cafe", "Other"];
const VOLUMES = ["Under 100 bars / month", "100–500 bars / month", "500–1,000 bars / month", "1,000+ bars / month", "Not sure yet"];
const WHO = [
  { t: "Retailers", d: "Shops and grocers looking for a real-food bar on the shelf." },
  { t: "Gyms", d: "Front-desk fuel for before and after training." },
  { t: "Cafes", d: "A grab-and-go bar next to the coffee." },
];

export default function Wholesale() {
  const { bind, submit, loading, done, setDone } = useSubmitForm("/wholesale", INITIAL, "Inquiry received. We'll be in touch.");
  return (
    <div data-testid="wholesale-page">
      <PageHero testId="wholesale-hero" eyebrow="Wholesale" lines={["Stock", "DanoBars."]} intro="We're finalizing recipes now. Tell us about your business and we'll reach out about availability, pricing and timing." />
      <section className="mx-auto grid max-w-[1440px] gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:px-12 pb-24 sm:pb-32">
        <div className="lg:col-span-4">
          <Reveal><Eyebrow>Who we're talking to</Eyebrow></Reveal>
          <div className="mt-6 space-y-3">
            {WHO.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.08}>
                <div className="rounded-[24px] border-2 border-dano-ink p-6 transition-colors duration-500 hover:bg-dano-gold">
                  <h3 className="font-wide text-2xl uppercase">{w.t}</h3>
                  <p className="mt-2 text-dano-ink/80">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          {done ? (
            <FormSuccess testId="wholesale-success" title="Thanks, got it." text="Your inquiry is saved. Dany or Rick will reply by email." onReset={() => setDone(false)} />
          ) : (
            <Reveal>
              <form onSubmit={submit} data-testid="wholesale-form" className="grid gap-5 rounded-[28px] bg-dano-sand/70 p-6 sm:grid-cols-2 sm:p-10">
                <Field label="Business name" required testId="wholesale-business-name-input" {...bind("business_name")} />
                <SelectField label="Business type" options={TYPES} testId="wholesale-business-type-select" {...bind("business_type")} />
                <Field label="Contact name" required testId="wholesale-contact-name-input" {...bind("contact_name")} />
                <Field label="Email" type="email" required testId="wholesale-email-input" {...bind("email")} />
                <Field label="Phone" type="tel" testId="wholesale-phone-input" {...bind("phone")} />
                <Field label="Location (city, state)" required testId="wholesale-location-input" {...bind("location")} />
                <SelectField label="Estimated volume" options={VOLUMES} testId="wholesale-volume-select" className="sm:col-span-2" {...bind("estimated_volume")} />
                <Field label="Message" textarea testId="wholesale-message-input" className="sm:col-span-2" placeholder="Tell us about your space and customers" {...bind("message")} />
                <button type="submit" disabled={loading} data-testid="wholesale-form-submit" className="btn-press sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-dano-red py-4 font-cond text-lg uppercase tracking-widest text-dano-cream hover:bg-dano-ink disabled:opacity-70">
                  {loading && <Loader2 className="h-5 w-5 animate-spin" />} Send inquiry
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
